import { ptBR } from 'date-fns/locale'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { Calendar as CalendarIcon } from 'lucide-react'
import { useState } from 'react'

import { BirthDateInput } from '@/_components/inputs/birth-date-input'
import { Button, Calendar, Popover, PopoverContent, PopoverTrigger } from '@/_components/ui'
import { cn } from '@/_lib/utils'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

const DATE_INPUT_FORMATS = ['DD/MM/YYYY', 'YYYY-MM-DD']

interface BirthDatePickerProps {
  // DD/MM/YYYY (partial values while typing are passed through unchanged).
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  className?: string
  hasError?: boolean
}

// A birth-date field combining a masked keyboard input (DD/MM/YYYY) with a
// calendar popover bounded to past dates. Both entry paths keep the value in
// DD/MM/YYYY so they stay in sync.
export const BirthDatePicker = ({
  value,
  onChange,
  disabled,
  className,
  hasError,
}: BirthDatePickerProps) => {
  const timeZone = dayjs.tz.guess()
  const [open, setOpen] = useState(false)

  const parsed = dayjs(value, DATE_INPUT_FORMATS, true)
  const selectedDate = parsed.isValid() ? parsed.toDate() : undefined

  // No future birth dates; no lower bound so any past date is selectable.
  const maximumSelectableDate = dayjs().tz(timeZone).endOf('day').toDate()

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <BirthDateInput
          value={value}
          onChange={onChange}
          disabled={disabled}
          placeholder="DD/MM/AAAA"
          className={cn(
            'pr-10',
            hasError && 'border-aux-red-danger-5 focus-visible:ring-aux-red-danger-5',
            className,
          )}
        />
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label="Abrir calendário"
            className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-brand-grayscale-8 hover:bg-brand-grayscale-2"
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent className="w-auto p-0" align="end">
        <Calendar
          mode="single"
          selected={selectedDate}
          defaultMonth={selectedDate}
          onSelect={(date) => {
            if (!date) return
            onChange(dayjs(date).format('DD/MM/YYYY'))
            setOpen(false)
          }}
          locale={ptBR}
          timeZone={timeZone}
          captionLayout="dropdown"
          startMonth={new Date(1900, 0)}
          endMonth={new Date()}
          disabled={{ after: maximumSelectableDate }}
        />
      </PopoverContent>
    </Popover>
  )
}
